import { get, isEmpty } from 'lodash-es';
import moment from 'moment-timezone';
import React, { Fragment, ReactNode } from 'react';

import { sanitize } from 'core/services/utils';
import { ContextActionContainer } from 'shared/components/internal/ContextAction';
import { DownloadLink } from 'shared/components/internal/DownloadLink';

import { RecordType, SchemaVersionType } from '../shared';
import css from './recordHelpers.module.scss';
import { getRecordRenderMap } from './recordMap';

export function UppercaseRenderer(value: ReactNode) {
  return <span className={css.uppercase}>{value}</span>;
}

export function BooleanRenderer(value: ReactNode) {
  return value ? 'True' : 'False';
}

export function SipHeadersRenderer(headers: any) {
  return (
    <pre className={css.preformatted}>
      {headers.CALL_ID && <div>{`Call-ID: ${sanitize(headers.CALL_ID)}`}</div>}
      {headers.SEQ && <div>{`CSeq: ${sanitize(headers.SEQ)}`}</div>}
      {headers.USER_AGENT && (
        <div>{`User-Agent: ${sanitize(headers.USER_AGENT)}`}</div>
      )}
      {headers.CONTENT_TYPE && (
        <div>{`Content-Type: ${sanitize(headers.CONTENT_TYPE)}`}</div>
      )}
      {headers.DATE && <div>{`Date: ${sanitize(headers.DATE)}`}</div>}
      {headers.REPLY_TO && (
        <div>{`Reply-To: ${sanitize(headers.REPLY_TO)}`}</div>
      )}
      {headers.SUBJECT && <div>{`Subject: ${sanitize(headers.SUBJECT)}`}</div>}
    </pre>
  );
}

export function SipRequestRenderer(request: any) {
  return (
    <pre className={css.preformatted}>
      {request.method && (
        <div>{`${request.method} ${sanitize(request.uri)} SIP/2.0`}</div>
      )}
      {request.to && <div>{`To: ${sanitize(request.to)}`}</div>}
      {request.from && <div>{`From: ${sanitize(request.from)}`}</div>}
      {request.body_length && (
        <div>{`Content-Length: ${sanitize(request.body_length)}`}</div>
      )}
      {request.path && <div>{`Path: ${sanitize(request.path)}`}</div>}
    </pre>
  );
}

export function DnsAnswerRenderer(values: any) {
  return (
    <table>
      <thead>
        <tr>
          <th>Answer</th>
          <th>TTL (Seconds)</th>
        </tr>
      </thead>
      <tbody>
        {values &&
          values.forEach((entry) => (
            <tr>
              <td>{entry.rr}</td>
              <td>{entry.ttl}</td>
            </tr>
          ))}
      </tbody>
    </table>
  );
}

export function HashRenderer(hash: any) {
  return (
    <div>
      {Object.keys(hash).map((key) => (
        <div key={`hash-${key}-${hash[key]}`}>
          <span>{key}: </span>
          <span>{hash[key]}</span>
        </div>
      ))}
    </div>
  );
}

export function StoredFilePathRenderer(value: any) {
  return (
    <DownloadLink
      downloadPath={`/api/v1/extracted-file?filename=${value}`}
      fileName={value}
      key='file-download'
    >
      Download File
    </DownloadLink>
  );
}

export function DataChannelRenderer(value: any) {
  return (
    <Fragment>
      {`Source: ${value.src.address}`}
      <br />
      {`Destination: ${value.dst.address}`}
    </Fragment>
  );
}

export function PendingCommandsRenderer(pendingCommands: any[]) {
  return pendingCommands.map((entry, index) => (
    <div key={`pending-command-${entry.cmd}-${entry.arg}`}>
      <div>{`${entry.cmd} ${entry.arg}`}</div>
      {index < pendingCommands.length - 1 && <br />}
    </div>
  ));
}

export function renderHeaders(headers: any) {
  return !isEmpty(headers)
    ? Object.keys(headers).reduce((headerAccumulator: any[], key: any) => {
        if (!isEmpty(headers[key])) {
          const headerKey = key
            .split('-')
            .map(
              (str) =>
                str.charAt(0) + str.substring(1, str.length).toLocaleLowerCase()
            )
            .join('-');
          headerAccumulator.push(headerKey, ': ', headers[key], '\n');
        }
        return headerAccumulator;
      }, [])
    : '';
}

export function HttpRequestRenderer(request: any) {
  return (
    <pre className={css.preformatted}>
      {sanitize(request.method)} {request.uri}
      {' HTTP/1.1\n'}
      {renderHeaders(request.headers)}
    </pre>
  );
}

export function HttpResponseRenderer(response: any) {
  return (
    <pre className={css.preformatted}>
      {'HTTP/1.1 '}
      {response.status_code} {sanitize(response.status_msg)}
      {'\n'}
      {renderHeaders(response.headers)}
    </pre>
  );
}

export function SanitizedRenderer(value: any) {
  return sanitize(value);
}

export function CertSubjectRenderer(value: any) {
  return `${value.subject} | ${value.fuid}`;
}

export function MetadataRenderer(metadata: any) {
  if (isEmpty(metadata)) {
    return '';
  }

  const metadataEntries: ReactNode[] = [];

  Object.keys(metadata).forEach((key, index) => {
    if (key === 'storedFilePath') {
      metadataEntries.push(
        <Fragment key={`metadata-${index}`}>
          File Download
          <DownloadLink
            downloadPath={`/api/v1/extracted-file?filename=${metadata[key]}`}
            fileName={metadata[key]}
          >
            Download
          </DownloadLink>
        </Fragment>
      );
    } else if (!isEmpty(metadata[key])) {
      metadataEntries.push(
        <Fragment key={`metadata-${index}`}>
          <div>{key}</div>
          <div>{metadata[key]}</div>
        </Fragment>
      );
    }
  });

  return metadataEntries;
}

export function HeadersRenderer(headers: any) {
  const smtpContent = [
    ['HELO:', headers.helo],
    ['MAIL FROM:', headers.mailfrom],
    ['RCPT TO:', headers.rcptto],
    ['Date:', headers.date],
    ['From:', headers.from],
    ['To:', headers.to],
    ['Reply-To:', headers.reply_to],
    ['Message-ID:', headers.msg_id],
    ['In-Reply-To:', headers.in_reply_to],
    ['Subject:', headers.subject],
    ['X-Originating-IP:', headers.x_originating_ip],
    ['Received:', headers.first_received],
    ['Received:', headers.second_received],
  ];

  return (
    <pre className={css.preformatted}>
      {smtpContent.map((entry) =>
        entry[1] ? `${entry[0]} ${entry[1].toString()}\n` : ''
      )}
    </pre>
  );
}

export const parseTimestamp = (
  timestamp: number,
  schemaVersion: SchemaVersionType = 'legacy'
) => {
  return schemaVersion === 'v3' ? moment(timestamp) : moment.unix(timestamp);
};

export function infoRenderer(
  type: string,
  formatStr: string,
  _value: any,
  record: RecordType
) {
  const response = formatStr.split(/{(.*?)}/g).map((parsedKey, index) => {
    const matchedValue = get(record, parsedKey);
    let result = parsedKey;
    if (matchedValue !== undefined) {
      const renderMap = getRecordRenderMap()[type];
      const renderer =
        renderMap.hasOwnProperty(parsedKey) &&
        renderMap[parsedKey] &&
        renderMap[parsedKey].renderer;

      result = renderer ? renderer(matchedValue, record) : matchedValue;
    }

    return (
      <ContextActionContainer
        className={css.result}
        contextData={record}
        eventTimestamp={
          record.timestamp
            ? moment.unix(record.timestamp / 1000).toDate()
            : undefined
        }
        hideDots={true}
        key={`info-${index}`}
      >
        {result}
      </ContextActionContainer>
    );
  });

  return response;
}
